/**
 * Alert Context for displaying notifications across the application
 */

import { createContext, useContext, useState, ReactNode, useCallback, useEffect } from 'react';

export interface AlertOptions {
  message: string;
  type?: 'success' | 'error' | 'warning' | 'info';
  title?: string;
  duration?: number;
  onConfirm?: () => void;  // Callback when user confirms
  showConfirm?: boolean;   // Show confirm button
}

interface AlertContextType {
  showAlert: (options: AlertOptions) => void;
  hideAlert: () => void;
  alert: AlertOptions | null;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export function AlertProvider({ children }: { children: ReactNode }) {
  const [alert, setAlert] = useState<AlertOptions | null>(null);

  const showAlert = useCallback((options: AlertOptions) => {
    setAlert(options);
    
    // Auto hide after duration (default 5 seconds) only if no confirm button
    const duration = options.duration || 5000;
    if (duration > 0 && !options.showConfirm) {
      setTimeout(() => {
        setAlert(null);
      }, duration);
    }
  }, []);

  const hideAlert = useCallback(() => {
    setAlert(null);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const originalAlert = window.alert;
    window.alert = (message?: string) => {
      showAlert({
        message: message ? String(message) : '',
        type: 'info',
        duration: 5000,
      });
    };

    return () => {
      window.alert = originalAlert;
    };
  }, [showAlert]);

  return (
    <AlertContext.Provider value={{ showAlert, hideAlert, alert }}>
      {children}
      {alert && (
        <div
          className="fixed bottom-6 left-4 right-4 z-[200] mx-auto max-w-xs shadow-2xl rounded-2xl border border-gray-200 bg-white px-5 py-4 text-sm text-gray-900 animate-[fadeIn_0.25s_ease-out] sm:left-auto sm:right-6"
          role="alert"
          onClick={hideAlert}
        >
          {alert.title && <div className="font-semibold mb-1">{alert.title}</div>}
          <div className="leading-6">{alert.message}</div>
        </div>
      )}
    </AlertContext.Provider>
  );
}

export function useAlert() {
  const context = useContext(AlertContext);
  if (context === undefined) {
    throw new Error('useAlert must be used within an AlertProvider');
  }
  return context;
}

